import { createContext, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { useUser } from './user-context';
import { api } from '../../api/client';

// Fallback estático mientras se carga /api/sedes. Coincide con apps/api/scripts/seed.js.
export const BRANCHES = [
  'Bogotá - Park Way',
  'Bogotá - Usaquén',
];

export interface Sede {
  id: number;
  codigo: string;
  nombre: string;
  activa: boolean;
  // Vienen de /api/sedes (JOIN entidades_legales). Opcionales porque el
  // fallback estatico no los tiene.
  razon_social?: string;
  nit?: string;
  dv?: string;
}

interface BranchContextType {
  currentBranch: string;
  currentBranchId: number | null;
  setCurrentBranch: (branch: string) => void;
  branches: string[];
  sedes: Sede[];
}

const CLAVE_SEDE = 'salonika_sede_actual';
// Última lista de sedes que respondió el servidor. Sin ella, un arranque sin
// internet deja currentBranchId en null y la caja de contingencia no sabe de
// qué sede es (modo contingencia, oct-2026).
const CLAVE_SEDES = 'salonika_sedes';

function sedesGuardadas(): Sede[] {
  try {
    const s = JSON.parse(localStorage.getItem(CLAVE_SEDES) || '[]');
    return Array.isArray(s) ? s : [];
  } catch {
    return [];
  }
}

const BranchContext = createContext<BranchContextType | undefined>(undefined);

export function BranchProvider({ children }: { children: ReactNode }) {
  const { user } = useUser();
  const [sedes, setSedes] = useState<Sede[]>(sedesGuardadas);
  // La sede elegida se recuerda en el navegador. Antes vivia solo en memoria:
  // cualquier recarga o pestaña nueva volvia a la primera sede (Park Way) sin
  // avisar, y en pantallas como "Llaves y credenciales" eso es cargar llaves
  // en la entidad legal equivocada.
  const [currentBranch, setCurrentBranchState] = useState<string>(() => {
    try { return localStorage.getItem(CLAVE_SEDE) || BRANCHES[0]; } catch { return BRANCHES[0]; }
  });
  const setCurrentBranch = (branch: string) => {
    setCurrentBranchState(branch);
    try { localStorage.setItem(CLAVE_SEDE, branch); } catch { /* sin storage: solo memoria */ }
  };

  // Cargar sedes desde el backend una vez el usuario esté autenticado
  useEffect(() => {
    if (!user) return;
    api.get<Sede[]>('/api/sedes')
      .then((data) => {
        const activas = data.filter(s => s.activa);
        setSedes(activas);
        try { localStorage.setItem(CLAVE_SEDES, JSON.stringify(activas)); } catch { /* solo memoria */ }
        const nombres = activas.map(s => s.nombre);
        if (nombres.length > 0) {
          setCurrentBranchState((prev) => (nombres.includes(prev) ? prev : nombres[0]));
        }
      })
      .catch(() => { /* fallback al hardcoded de BRANCHES */ });
  }, [user]);

  const branches = useMemo(() => (sedes.length > 0 ? sedes.map(s => s.nombre) : BRANCHES), [sedes]);
  const currentBranchId = useMemo(() => {
    return sedes.find(s => s.nombre === currentBranch)?.id ?? null;
  }, [sedes, currentBranch]);

  return (
    <BranchContext.Provider value={{ currentBranch, currentBranchId, setCurrentBranch, branches, sedes }}>
      {children}
    </BranchContext.Provider>
  );
}

export function useBranch() {
  const context = useContext(BranchContext);
  if (context === undefined) {
    throw new Error('useBranch must be used within a BranchProvider');
  }
  return context;
}
